import moment from "moment-timezone";
import PropTypes from "prop-types";
import {
  FILTER_TYPE_NONE,
  FILTER_TYPE_MULTI_SELECT_NOLABEL,
  COL_CODE_CONTROL_NUMBER,
  FILTER_TYPE_TEXT,
  FORMATTER_RIGHT_ALIGN,
  COL_CODE_FULL_NAME,
  FORMATTER_INDENTED,
  COL_CODE_STATE,
} from "components/Shared/MGrid.tsx";
import {
  DATA_TYPE_DATE,
  DATA_TYPE_NUMBER_RAW,
  DATA_TYPE_TEXT_BUTTON,
  DATA_TYPE_DATE_WITHDOW,
  DATA_TYPE_NUMBER,
  DATA_TYPE_STRING,
  DATA_TYPE_MONEY,
  DATA_TYPE_PERCENT_DECIMAL,
  DATA_TYPE_NUMBER_HUNDREDTHS,
} from "typeConstants/dataTypes";
import {
  dateTimeToStringWithDOW,
  dateToStringWithDOW,
  dateToString,
  moneyFormatter,
  numberWithCommasFormatter,
  formatDecimalPercent2,
} from "components/Shared/Formatters";
import { filterOnFilterDate } from "components/Homeowner/History/DataFormatters";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import React from "react";
import { CaseTimeLogWorkTypeEnum } from "interfaces/Mueller.Business.Models.ts";
import { payrateTypes } from "typeConstants/payrateTypes";

export const FORMATTER_EMPTYCELL_RED = ({ value }) => {
  let newValue = value ? value : "missing";
  return <div style={value ? {} : { color: "#c8102e", fontWeight: "bold" }}>{newValue}</div>;
};

FORMATTER_EMPTYCELL_RED.propTypes = {
  value: PropTypes.string,
};

export const updateIcon = <Icon sx={{ color: "var(--crimson)" }}>update</Icon>;

export const clockInOutColDefs = [
  {
    key: "date",
    name: "Date",
    width: 150,
    dataSource: "date",
    dataType: DATA_TYPE_DATE_WITHDOW,
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "in",
    name: "In",
    width: 100,
    dataSource: "in",
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "out",
    name: "Out",
    width: 100,
    dataSource: "out",
    filterType: FILTER_TYPE_NONE,
    formatter: FORMATTER_EMPTYCELL_RED,
  },
  {
    key: "hours",
    name: "Hours",
    width: 100,
    dataSource: "hours",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Hours</div>,
  },
];

export const clockInOutColDefsWithEditButtons = [
  {
    key: "date",
    name: "Date",
    width: 100,
    dataSource: "date",
    filterType: FILTER_TYPE_NONE,
    tooltip: (row) => row.rawData + (row.wasEdited ? " [Edited]" : ""),
  },
  {
    key: "wasEditedIcon",
    name: (
      <div title="Edited" style={{ textAlign: "center" }}>
        {updateIcon}
      </div>
    ),
    width: 40,
    dataSource: "wasEdited",
    filterType: FILTER_TYPE_NONE,
    tooltip: (row) => (row.wasEdited ? "Edited" : ""),
  },
  {
    key: "in",
    name: "In",
    width: 70,
    dataSource: "in",
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "out",
    name: "Out",
    width: 70,
    dataSource: "out",
    filterType: FILTER_TYPE_NONE,
    formatter: FORMATTER_EMPTYCELL_RED,
  },
  {
    key: "hours",
    name: "Hours",
    width: 50,
    dataSource: "hours",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Hours</div>,
  },
  {
    key: "button",
    name: "",
    width: 100,
    dataSource: "button",
    filterType: FILTER_TYPE_NONE,
    tooltip: () => null,
  },
  {
    key: "wasEdited",
    dataSource: "wasEdited",
    visible: false,
  },
  {
    key: "rawData",
    dataSource: "rawData",
    visible: false,
  },
];
export const appraiserPerformanceColDefs = [
  {
    name: "Control #",
    code: COL_CODE_CONTROL_NUMBER,
    dataType: DATA_TYPE_TEXT_BUTTON,
    dataSource: "controlIdNumber",
    filterType: FILTER_TYPE_NONE,
    tooltip: "Click to view case",
  },
  {
    key: "surveyTypeName",
    name: "Survey Type",
    width: 200,
    dataSource: "surveyTypeName",
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "previousMinutes",
    name: "Previous Mins",
    width: 120,
    dataSource: "workTypePreviousMinutesForUser",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Previous Mins</div>,
  },
  {
    key: "currentMinutes",
    name: "Current Mins",
    width: 120,
    dataSource: "timeMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Current Mins</div>,
  },
  {
    key: "totalMinutes",
    name: "Total Mins",
    width: 120,
    dataSource: "totalMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Total Mins</div>,
  },
  {
    key: "expectedMinutes",
    name: "Expected Mins",
    width: 120,
    dataSource: "appraisalExpectedMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Expected Mins</div>,
  },
  {
    key: "isAppraiserIncompleteIcon",
    name: (
      <div title="Appraisal Has Never Been Submitted By Appraiser Yet" style={{ textAlign: "center" }}>
        <Icon className="qa-list-is-incomplete-appraiser">cancel</Icon>
      </div>
    ),
    width: 40,
    dataSource: "isAppraiserIncompleteIcon",
    filterType: FILTER_TYPE_NONE,
    tooltip: (row) => (row.appraisalStatusName ? `${row.appraisalStatusName}` : "No appraisal status"),
  },
];
export const productionTimeColDefs = [
  {
    code: COL_CODE_CONTROL_NUMBER,
    dataSource: "controlIdNumber",
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "loggedTime",
    name: "Minutes",
    width: 100,
    dataSource: "timeMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Minutes</div>,
  },
  {
    key: "date",
    name: "Date",
    width: 110,
    dataSource: "workDate",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_DATE_WITHDOW,
  },
  {
    key: "workType",
    name: "Work Type",
    width: 150,
    dataSource: "workTypeName",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_STRING,
  },
];
export const productionTimeColDefsWithEditButtons = [
  ...productionTimeColDefs,
  {
    key: "button",
    name: "",
    width: 100,
    dataSource: "button",
    filterType: FILTER_TYPE_NONE,
    tooltip: () => null,
  },
];
export const approvedToolTip = (row) =>
  row.isApproved
    ? `Approved by ${row.approvalDetail && row.approvalDetail.approvedByName} on ${row.approvalDetail && dateTimeToStringWithDOW(row.approvalDetail.approvedDateTime)}`
    : "Not Approved";

export const isTotalRow = (row) => row.internetUserId === -1 && row.employeeName?.firstName === "Total";

const getFilterDiv = (title, value) => {
  return (
    <div title={`${title}: ${value}`} style={{ textAlign: "right" }}>
      {value}
    </div>
  );
};

export const busOpsEmployeeColDefs = (data) => {
  // Totals are added to the list as a fake user
  const totals = data.find(isTotalRow);

  // Row tooltips
  const getAuditSummary = (auditTotalPercent) => {
    return auditTotalPercent
      ? `${auditTotalPercent.passedCount} Pass / ${auditTotalPercent.totalCount} Total`
      : "0 Pass / 0 Total";
  };

  const getRejectionSummary = (rejectionPercentage) => {
    return rejectionPercentage
      ? `${rejectionPercentage.rejectionCount} Rejections / ${rejectionPercentage.totalCount} Total`
      : "0 Pass / 0 Total";
  };

  const formatDecimalPercent = formatDecimalPercent2(0, true);

  const getAuditValue = (audit) => {
    if (!audit?.totalCount) return "--";
    else return formatDecimalPercent(audit.amount);
  };

  const getRejectionValue = (rejection) => {
    if (!rejection?.totalCount) return "--";
    else return formatDecimalPercent(rejection.amount);
  };

  // Filter Renderers
  const getAverageWeeksEmployeed = () => getFilterDiv("Average Weeks Employed", totals.weeksEmployed);
  const getTotalAvailableHours = () => {
    const totalHours = totals.availableHours ?? 0;
    return getFilterDiv("Total Available Hours", totalHours);
  };
  const getTotalClockInHours = () => {
    const totalHours = totals.payrollClockInTimeHours ?? 0;
    return getFilterDiv("Total Clock In Hours", totalHours);
  };
  const getTotalExpectedHours = () => {
    const totalHours = Number(totals.totalExpectedMinutes / 60).toFixed(2);
    return getFilterDiv("Total Expected Hours", totalHours);
  };
  const getTotalQaReviewExpectedMinutes = () => {
    const totalHours = Number(totals.qaReviewExpectedMinutes / 60).toFixed(2);
    return getFilterDiv("Total QAHE Comp Hours", totalHours);
  };
  const getTotalAuditExpectedMinutes = () => {
    const totalHours = Number(totals.auditExpectedMinutes / 60).toFixed(2);
    return getFilterDiv("Total QAHE Audit Hours", totalHours);
  };
  const getTotalPhoneSupportExpectedMinutes = () => {
    const totalHours = Number(totals.phoneSupportExpectedMinutes / 60).toFixed(2);
    return getFilterDiv("Total Phone Supp Hours", totalHours);
  };
  const getTotalExpectedCost = () => {
    const totalExpected = Number(totals.expectedCost).toFixed(2);
    return getFilterDiv("Total Expected Cost", `$${totalExpected}`);
  };
  const getTotalRevenue = () => {
    const value = Number(totals.revenue).toFixed(2);
    return getFilterDiv("Total Revenue", `$${value}`);
  };
  const getTotalActualCost = () => {
    const totalActual = Number(totals.actualCost).toFixed(2);
    return getFilterDiv("Total Actual Cost", `$${totalActual}`);
  };
  const getTotalCostDifference = () => {
    const totalDiff = Number(totals.costDifference).toFixed(2);
    return getFilterDiv("Total Cost Diff", `$${totalDiff}`);
  };
  const getAverageCostEfficiency = () =>
    getFilterDiv("Average Cost Eff", `${Number(totals.costEfficiency * 100).toFixed(0)}%`);
  const getAverageTimeEfficiency = () =>
    getFilterDiv("Average Time Eff", `${Number(totals.timeEfficiency * 100).toFixed(0)}%`);
  const getAverageAuditPercentage = () => {
    const auditPercent = totals.auditPercentage ? totals.auditPercentage.weightedPassingPercent : 0;
    return getFilterDiv("Weighted Audit 7", `${Number(auditPercent * 100).toFixed(0)}%`);
  };
  const getAverageAuditPercentage30 = () => {
    const auditPercent = totals.auditPercentage30 ? totals.auditPercentage30.weightedPassingPercent : 0;
    return getFilterDiv("Weighted Audit 30", `${Number(auditPercent * 100).toFixed(0)}%`);
  };
  const getAverageRejectionPercentage = () =>
    getFilterDiv("Average Rejection Percentage", `${Number(totals.rejectionPercentage.rejectionCount).toFixed(0)}%`);
  const getAverageRejectionAuditPercentage = () => {
    const auditPercent = totals.rejectionAuditPercentage ? totals.rejectionAuditPercentage.weightedPassingPercent : 0;
    return getFilterDiv("Weighted Rejection Audit 7", `${Number(auditPercent * 100).toFixed(0)}%`);
  };
  const getAverageRejectionAuditPercentage30 = () => {
    const auditPercent = totals.rejectionAuditPercentage30
      ? totals.rejectionAuditPercentage30.weightedPassingPercent
      : 0;
    return getFilterDiv("Weighted Rejection Audit 30", `${Number(auditPercent * 100).toFixed(0)}%`);
  };

  return [
    {
      key: "IsClockedInNowIcon",
      name: (
        <div title="Clocked In" style={{ textAlign: "center" }}>
          <Icon color="primary" className="qa-list-is-clocked-in">
            lens
          </Icon>
        </div>
      ),
      width: 40,
      dataSource: "IsClockedInNowIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: (row) => (row.isClockedInNow ? "Clocked In" : ""),
      visible: true,
      hideFromExport: true,
    },
    {
      code: COL_CODE_FULL_NAME,
      key: "employeeName.firstLast",
      name: "Employee Name",
      dataSource: "employeeName.firstLast",
      filterType: FILTER_TYPE_TEXT,
      visible: true,
      width: 175,
    },
    {
      key: "supervisorName",
      name: "Manager",
      dataSource: "supervisorName",
      visible: false,
    },
    {
      key: "directorName",
      name: "Director",
      dataSource: "directorName",
      visible: false,
    },
    {
      key: "isApprovedIcon",
      name: (
        <div title="Approved" style={{ textAlign: "center" }}>
          <Icon color="primary">check_circle_outline</Icon>
        </div>
      ),
      width: 40,
      dataSource: "isApprovedIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: approvedToolTip,
      visible: true,
      hideFromExport: true,
    },
    {
      key: "isMissingClockInfoIcon",
      name: (
        <div title="Missing Clock Out" style={{ textAlign: "center" }}>
          <Icon color="primary">timer_off</Icon>
        </div>
      ),
      width: 40,
      dataSource: "isMissingClockInfoIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: (row) => (row.isMissingClockInfo ? "Missing Clock Out" : ""),
      visible: true,
      hideFromExport: true,
    },
    {
      key: "employmentType",
      name: "FT/PT",
      dataSource: "employmentType",
      filterType: FILTER_TYPE_TEXT,
      width: 55,
      visible: true,
    },
    {
      key: "activeStatus",
      name: "Active/Inactive",
      dataSource: "activeStatus",
      filterType: FILTER_TYPE_TEXT,
      width: 100,
      visible: true,
    },
    {
      key: "grade",
      name: "Grade",
      dataSource: "grade",
      filterType: FILTER_TYPE_TEXT,
      visible: true,
      width: 40,
    },
    {
      key: "weeksEmployed",
      name: "Tenure",
      width: 55,
      dataSource: "weeksEmployed",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Weeks in Production",
      filterRenderer: totals ? () => getAverageWeeksEmployeed() : null,
      tooltip: () => "Weeks Employed",
    },
    {
      key: "revenue",
      name: "Revenue",
      width: 75,
      dataSource: "revenue",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Revenue - Pay Period",
      filterRenderer: totals ? () => getTotalRevenue() : null,
      tooltip: () => "Revenue",
    },
    {
      key: "payrollClockInTimeHours",
      name: "Clock In Hrs",
      width: 80,
      dataSource: "payrollClockInTimeHours",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals ? () => getTotalClockInHours() : null,
      tooltip: () => "Clock In Hrs",
    },
    {
      key: "availableHours",
      name: "Available Hours",
      width: 80,
      dataSource: "availableHours",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals ? () => getTotalAvailableHours() : null,
      tooltip: () => "Available Hours",
    },
    {
      key: "totalExpectedMinutes",
      name: "Expected Hrs",
      width: 80,
      dataSource: (row) => (row.totalExpectedMinutes ? Number(row.totalExpectedMinutes / 60).toFixed(2) : 0),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "QAHE Completed + QAHE Audited + Phone Support Expected Hours",
      filterRenderer: totals ? () => getTotalExpectedHours() : null,
      tooltip: () => "Expected Hrs",
    },
    {
      key: "qaReviewExpectedMinutes",
      name: "QAHE Comp.",
      width: 80,
      dataSource: (row) => (row.qaReviewExpectedMinutes ? Number(row.qaReviewExpectedMinutes / 60).toFixed(2) : 0),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals ? () => getTotalQaReviewExpectedMinutes() : null,
      tooltip: () => "QAHE Comp.",
    },
    {
      key: "auditExpectedMinutes",
      name: "QAHE Audit",
      width: 80,
      dataSource: (row) => (row.auditExpectedMinutes ? Number(row.auditExpectedMinutes / 60).toFixed(2) : 0),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals ? () => getTotalAuditExpectedMinutes() : null,
      tooltip: () => "QAHE Audit",
    },
    {
      key: "phoneSupportExpectedMinutes",
      name: "Phone Supp.",
      width: 80,
      dataSource: (row) =>
        row.phoneSupportExpectedMinutes ? Number(row.phoneSupportExpectedMinutes / 60).toFixed(2) : 0,
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals ? () => getTotalPhoneSupportExpectedMinutes() : null,
      tooltip: () => "Phone support expected minutes",
    },
    {
      key: "expectedCost",
      name: "Expect. Cost",
      width: 80,
      dataSource: (row) => (row.expectedCost ? Number(row.expectedCost).toFixed(2) : 0),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Expected Hours x Standard QA Wage",
      filterRenderer: totals ? () => getTotalExpectedCost() : null,
      tooltip: () => "Expected Cost",
    },
    {
      key: "actualCost",
      name: "Actual Cost",
      width: 80,
      dataSource: (row) => (row.actualCost ? Number(row.actualCost).toFixed(2) : 0),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      tooltip: (row) => row.hourlyPayRate ?? "Missing Hourly Wage",
      filterRenderer: totals ? () => getTotalActualCost() : null,
    },
    {
      key: "costDifference",
      name: "Cost Diff.",
      width: 80,
      dataSource: (row) => (row.costDifference ? Number(row.costDifference).toFixed(2) : 0),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Expected Cost - Actual Cost",
      filterRenderer: totals ? () => getTotalCostDifference() : null,
    },
    {
      key: "costEfficiency",
      name: "Cost Eff %",
      width: 70,
      dataSource: "costEfficiency",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Expected Cost / Actual Cost %",
      filterRenderer: totals ? () => getAverageCostEfficiency() : null,
    },
    {
      key: "timeEfficiency",
      name: "Time Eff %",
      width: 70,
      dataSource: "timeEfficiency",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Expected Hours / Clock In Hours %",
      filterRenderer: totals ? () => getAverageTimeEfficiency() : null,
    },
    {
      key: "auditPercentage.amount", // This key differs from the datasource for sorting purposes
      name: "Audit 7",
      width: 55,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      dataSource: (row) => getAuditValue(row.auditPercentage),
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      visible: true,
      headerTooltip: "Weighted Audit 7",
      tooltip: (row) => getAuditSummary(row.auditPercentage),
      filterRenderer: totals ? () => getAverageAuditPercentage() : null,
    },
    {
      key: "auditPercentage30.amount", // This key differs from the datasource for sorting purposes
      name: "Audit 30",
      width: 55,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      dataSource: (row) => getAuditValue(row.auditPercentage30),
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      visible: true,
      headerTooltip: "Weighted Audit 30",
      tooltip: (row) => getAuditSummary(row.auditPercentage30),
      filterRenderer: totals ? () => getAverageAuditPercentage30() : null,
    },
    {
      key: "rejectionPercentage.amount",
      name: "Rej",
      width: 50,
      dataSource: (row) => getRejectionValue(row.rejectionPercentage),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Total Rejection Percentage Last 30 Days",
      tooltip: (row) => getRejectionSummary(row.rejectionPercentage),
      filterRenderer: totals ? () => getAverageRejectionPercentage() : null,
    },
    {
      key: "rejectionAuditPercentage.amount",
      name: "Rej Q 7",
      width: 75,
      dataSource: (row) => getAuditValue(row.rejectionAuditPercentage),
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      visible: true,
      headerTooltip: "Weighted Rejection Audit 7",
      tooltip: (row) => getAuditSummary(row.rejectionAuditPercentage),
      filterRenderer: totals ? () => getAverageRejectionAuditPercentage() : null,
    },
    {
      key: "rejectionAuditPercentage30.amount",
      name: "Rej Q 30",
      width: 75,
      dataSource: (row) => getAuditValue(row.rejectionAuditPercentage30),
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      visible: true,
      headerTooltip: "Weighted Rejection Audit 30",
      tooltip: (row) => getAuditSummary(row.rejectionAuditPercentage30),
      filterRenderer: totals ? () => getAverageRejectionAuditPercentage30() : null,
    },
    {
      key: "daysSinceLastCheckIn",
      name: "Coached",
      dataType: DATA_TYPE_NUMBER,
      width: 70,
      dataSource: (row) =>
        row.daysSinceLastCheckIn > 0 || row.daysSinceLastCheckIn === 0 ? row.daysSinceLastCheckIn : "--",
      visible: false,
      tooltip: (row) => row.lastCheckInNote ?? "No Coaching Calls",
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      headerTooltip: "Days Since Last Coaching Call",
    },
  ];
};

export const saEmployeeColDefs = (data, totals) => {
  if (!data) return [];

  const hundrethsOrZero = (number, isMoney = false) => {
    if (isMoney) return !number ? "$ 0.00" : `$ ${hundrethsOrZero(number, false)}`;
    else return !number ? 0 : number.toFixed(2);
  };

  return [
    {
      key: "IsClockedInNowIcon",
      name: (
        <div title="Clocked In" style={{ textAlign: "center" }}>
          <Icon color="primary" className="qa-list-is-clocked-in">
            lens
          </Icon>
        </div>
      ),
      width: 40,
      dataSource: "IsClockedInNowIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: (row) => (row.isClockedInNow ? "Clocked In" : ""),
      visible: true,
      hideFromExport: true,
    },
    {
      code: COL_CODE_FULL_NAME,
      dataSource: "employeeName.firstLast",
      filterType: FILTER_TYPE_TEXT,
      visible: true,
    },
    {
      key: "approvedIcon",
      name: (
        <div title="Approved" style={{ textAlign: "center" }}>
          <Icon color="primary">check_circle_outline</Icon>
        </div>
      ),
      width: 40,
      dataSource: "isApprovedIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: approvedToolTip,
      visible: true,
      hideFromExport: true,
    },
    {
      key: "isMissingClockInfoIcon",
      name: (
        <div title="Missing Clock Out" style={{ textAlign: "center" }}>
          <Icon color="primary">timer_off</Icon>
        </div>
      ),
      width: 40,
      dataSource: "isMissingClockInfoIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: (row) => (row.isMissingClockInfo ? "Missing Clock Out" : ""),
      visible: true,
      hideFromExport: true,
    },
    {
      key: "tenure",
      name: "Tenure",
      headerTooltip: "Tenure",
      width: 80,
      dataSource: "weeksEmployed",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Average Weeks Employed", totals.weeksEmployed)),
    },
    {
      key: "clockedInTimeMinutes",
      name: "Clock In Hrs",
      headerTooltip: "Total clock in hours",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.clockedInTimeMinutes / 60),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Clock In Hrs", hundrethsOrZero(totals.clockedInTimeMinutes / 60))),
    },
    {
      key: "availableHours",
      name: "Available Hours",
      width: 100,
      dataSource: "availableHours",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals && (() => getFilterDiv("Total Available Hours", Number(totals.availableHours ?? 0).toFixed(2))),
    },
    {
      key: "expectedHours",
      name: "Expected Hrs",
      headerTooltip: "SAHE Scheduled + SAHE Submitted + CSHE Completed",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.expectedHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals && (() => getFilterDiv("Total Expected Hours", Number(totals.expectedHours ?? 0).toFixed(2))),
    },
    {
      key: "scheduledExpectedHours",
      name: "Appt (HE)",
      headerTooltip: "Total scheduling expected hours",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.scheduledExpectedHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() => getFilterDiv("Total scheduling expected hours", Number(totals.scheduledExpectedHours ?? 0).toFixed(2))),
    },
    {
      key: "submittedExpectedHours",
      name: "QA Submit (HE)",
      headerTooltip: "Total submission expected hours",
      width: 115,
      dataSource: (row) => hundrethsOrZero(row.submittedExpectedHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() => getFilterDiv("Total submission expected hours", Number(totals.submittedExpectedHours ?? 0).toFixed(2))),
    },
    {
      key: "incidentExpectedHours",
      name: "CS Note (HE)",
      headerTooltip: "Total completed customer service expected hours",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.incidentExpectedHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() =>
          getFilterDiv(
            "Total completed customer service expected hours",
            Number(totals.incidentExpectedHours ?? 0).toFixed(2),
          )),
    },
    {
      key: "webOrderExpectedHours",
      name: "Manual Order (HE)",
      headerTooltip: "Expected hours of user manually ordering a case",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.webOrderExpectedHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() =>
          getFilterDiv(
            "Total expected hours of user manually ordering a case",
            Number(totals.webOrderExpectedHours ?? 0).toFixed(2),
          )),
    },
    {
      key: "coachingExpectedHours",
      name: "Coaching (HE)",
      headerTooltip: "Total coaching call expected hours",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.coachingExpectedHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() =>
          getFilterDiv("Total coaching call expected hours", Number(totals.coachingExpectedHours ?? 0).toFixed(2))),
    },
    {
      key: "callHours",
      name: "Call Hours",
      headerTooltip: "Additional Required Call Hours",
      width: 100,
      dataSource: (row) => hundrethsOrZero(row.callHours),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Total Call Hours", Number(totals.callHours ?? 0).toFixed(2))),
    },
    {
      key: "expectedCost",
      name: "Expected Cost",
      headerTooltip: "Expected hours x standard SA wage",
      width: 125,
      dataSource: (row) => hundrethsOrZero(row.expectedCost),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Total Expected Cost", hundrethsOrZero(totals.expectedCost, true))),
    },
    {
      key: "actualCost",
      name: "Actual Cost",
      headerTooltip: "Actual Cost",
      width: 125,
      dataSource: (row) => hundrethsOrZero(row.payrollCost),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Total Actual Cost", hundrethsOrZero(totals.payrollCost, true))),
    },
    {
      key: "costDifference",
      name: "Cost Difference",
      headerTooltip: "Expected Cost - Actual Cost",
      width: 125,
      dataSource: (row) => hundrethsOrZero(row.costDifference),
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals && (() => getFilterDiv("Total Cost Difference", hundrethsOrZero(totals.costDifference, true))),
    },
    {
      key: "costEfficiency",
      name: "Cost Efficiency %",
      headerTooltip: "Expected Cost / Actual Cost",
      width: 125,
      dataSource: "costEfficiency",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals && (() => getFilterDiv("Total Cost Efficiency", `${hundrethsOrZero(totals.costEfficiency * 100)}%`)),
    },
    {
      key: "timeEfficiency",
      name: "Time Efficiency %",
      headerTooltip: "Expected Hours / Clock In Hours",
      width: 125,
      dataSource: "timeEfficiency",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals && (() => getFilterDiv("Total Time Efficiency", `${hundrethsOrZero(totals.timeEfficiency * 100)}%`)),
    },
    {
      key: "appointmentsCompleted",
      name: "Appointments Completed",
      headerTooltip: "Number of Appointments Completed",
      width: 125,
      dataSource: "appointmentsCompleted",
      visible: false,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Total Appointments Completed", totals.appointmentsCompleted)),
    },
    {
      key: "appointmentsScheduled",
      name: "Appointments Scheduled",
      headerTooltip: "Number of Appointments Scheduled",
      width: 125,
      dataSource: "appointmentsScheduled",
      visible: false,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Total Appointments Scheduled", totals.appointmentsScheduled)),
    },
    {
      key: "top5Appointments",
      name: "Top 5",
      headerTooltip: "Number of Appointments made with Top 5",
      width: 125,
      dataSource: "top5Appointments",
      visible: false,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: totals && (() => getFilterDiv("Total Top 5", totals.top5Appointments)),
    },
    {
      key: "percentTop5Appointments",
      name: "Top 5 %",
      headerTooltip: "Percent of Appointments made with Top 5",
      width: 125,
      dataSource: "percentTop5Appointments",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() =>
          getFilterDiv(
            "Total Percent of Appointments made with Top 5",
            `${hundrethsOrZero(totals.percentTop5Appointments * 100)}%`,
          )),
    },
    {
      key: "appointmentsCompletedOnTime",
      name: "Appointments COT",
      headerTooltip: "Number of Appointments Completed On Time",
      width: 125,
      dataSource: "appointmentsCompletedOnTime",
      visible: false,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals && (() => getFilterDiv("Total Appointments Completed On Time", totals.appointmentsCompletedOnTime)),
    },
    {
      key: "percentAppointmentsCompletedOnTime",
      name: "Appointments COT %",
      headerTooltip: "Percent of Appointments Completed On Time",
      width: 125,
      dataSource: "percentAppointmentsCompletedOnTime",
      visible: true,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer:
        totals &&
        (() =>
          getFilterDiv(
            "Total Percent Appointments Completed On Time",
            `${hundrethsOrZero(totals.percentAppointmentsCompletedOnTime * 100)}%`,
          )),
    },
    {
      key: "lastEmployeeNote",
      name: "Last TEAM Entry",
      filterType: FILTER_TYPE_NONE,
      dataSource: (row) =>
        row.lastEmployeeNote ? `${dateToString(row.lastEmployeeNoteDate)}: ${row.lastEmployeeNote}` : "",
    },
  ];
};

export const appraiserEmployeeColDefs = () => [
  {
    key: "anyIncompleteAppraiser",
    name: "Incomplete",
    dataSource: "anyIncompleteAppraiser",
    visible: false,
  },
  {
    key: "anyIncompleteAppraiserIcon",
    name: (
      <div title="Appraiser Has Time On Appraisals That Have Never Been Submitted" style={{ textAlign: "center" }}>
        <Icon className="qa-list-is-incomplete-appraiser">cancel</Icon>
      </div>
    ),
    width: 40,
    dataSource: "anyIncompleteAppraiserIcon",
    filterType: FILTER_TYPE_NONE,
    tooltip: (row) => (row.anyIncompleteAppraiser ? "Incomplete" : ""),
  },
  {
    code: COL_CODE_FULL_NAME,
    dataSource: "employeeName",
    filterType: FILTER_TYPE_TEXT,
  },
  {
    key: "appraiserPayrateType",
    name: "Pay Type",
    width: 75,
    filterType: FILTER_TYPE_NONE,
    dataSource: (row) => payrateTypes.find((t) => t.value === row.payrateType)?.code,
    tooltip: (row) => payrateTypes.find((t) => t.value === row.payrateType)?.label,
  },
  {
    key: "approved",
    name: "Is Approved",
    width: 40,
    dataSource: "isApproved",
    visible: false,
  },
  {
    key: "approvedByName",
    name: "Approved By",
    dataSource: "approvalDetail.approvedByName",
    visible: false,
  },
  {
    key: "approvedDate",
    name: "Approved On",
    dataSource: "approvalDetail.approvedDateTime",
    dataType: DATA_TYPE_DATE,
    visible: false,
  },
  {
    key: "approvedIcon",
    name: (
      <div title="Approved" style={{ textAlign: "center" }}>
        <Icon color="primary">check_circle_outline</Icon>
      </div>
    ),
    width: 40,
    dataSource: "isApprovedIcon",
    filterType: FILTER_TYPE_NONE,
    tooltip: approvedToolTip,
  },
  {
    key: "isMissingClockInfo",
    name: "Is Missing Clock Info",
    dataSource: "isMissingClockInfo",
    visible: false,
  },
  {
    key: "totalHours",
    name: "Hours",
    width: 55,
    dataSource: "totalHours",
    visible: true,
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER_RAW,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Hours</div>,
  },
  {
    key: "appraiserExpectedHours",
    name: "Exp Hrs",
    width: 50,
    dataSource: "appraiserExpectedHours",
    visible: false,
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Exp Hrs</div>,
  },
  {
    key: "appraiserTimeEfficiencyScore",
    name: "Score",
    width: 60,
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    dataSource: "appraiserTimeEfficiencyScore",
    visible: false,
  },
  {
    key: "appraiserTimeEfficiencyGradeLetter",
    name: "Grade",
    width: 60,
    filterType: FILTER_TYPE_MULTI_SELECT_NOLABEL,
    formatter: FORMATTER_INDENTED,
    dataSource: "appraiserTimeEfficiencyGradeLetter",
    visible: true,
  },
];

export const fieldRepEmployeeColDefs = (data) => {
  let summaryData = transformFieldRepManagerSummary(data);

  let nonProdFilterRenderer = (smry) => (
    <div title={`Total Non Production Costs: ${smry.totalNonProdCost}`} style={{ textAlign: "right" }}>
      {smry.totalNonProdCost}
    </div>
  );
  let totalHours = (smry) => (
    <div title={`Total Hours: ${smry.totalHours}`} style={{ textAlign: "right" }}>
      {smry.totalHours}
    </div>
  );
  let totalAdminHours = (smry) => (
    <div title={`Total Hours: ${smry.totalAdminHours}`} style={{ textAlign: "right" }}>
      {smry.totalAdminHours}
    </div>
  );
  let totalSubmittedHours = (smry) => (
    <div title={`Total Submitted FHE: ${smry.totalSubmittedHours}`} style={{ textAlign: "right" }}>
      {smry.totalSubmittedHours}
    </div>
  );
  let totalAssignedHours = (smry) => (
    <div title={`Total Assigned FHE: ${smry.totalAssignedHours}`} style={{ textAlign: "right" }}>
      {smry.totalAssignedHours}
    </div>
  );
  let totalExpectedCost = (smry) => (
    <div title={`Total Expected Cost: ${smry.totalExpectedCost}`} style={{ textAlign: "right" }}>
      {smry.totalExpectedCost}
    </div>
  );
  let totalExpectedCostToRevenue = (smry) => (
    <div title={`Total Expected Cost to Revenue: ${smry.totalExpectedCostToRevenue}`} style={{ textAlign: "right" }}>
      {smry.totalExpectedCostToRevenue}%
    </div>
  );
  let totalActualCost = (smry) => (
    <div title={`Total Actual Cost: ${smry.totalActualCost}`} style={{ textAlign: "right" }}>
      {smry.totalActualCost}
    </div>
  );
  let totalTotalCost = (smry) => (
    <div title={`Total Total Cost: ${smry.totalTotalCost}`} style={{ textAlign: "right" }}>
      {smry.totalTotalCost}
    </div>
  );
  let totalActualCostToRevenue = (smry) => (
    <div title={`Total Actual Cost to Revenue: ${smry.totalActualCostToRevenue}`} style={{ textAlign: "right" }}>
      {smry.totalActualCostToRevenue}%
    </div>
  );
  let averageWeeksEmployed = (smry) => (
    <div title={`Average Weeks Employed: ${smry.averageWeeksEmployed}`} style={{ textAlign: "right" }}>
      {smry.averageWeeksEmployed}
    </div>
  );
  let totalCostEfficiency = (smry) => (
    <div title={`Total Cost Efficiency: ${smry.totalCostEfficiency}`} style={{ textAlign: "right" }}>
      {smry.totalCostEfficiency}%
    </div>
  );
  let totalFieldProductionCostDifference = (smry) => (
    <div title={`Total Cost Difference: ${smry.totalFieldProductionCostDifference}`} style={{ textAlign: "right" }}>
      {smry.totalFieldProductionCostDifference}
    </div>
  );
  let totalExpectedFieldServiceRevenue = (smry) => (
    <div title={`Total Revenue: ${smry.totalExpectedFieldServiceRevenue}`} style={{ textAlign: "right" }}>
      {smry.totalExpectedFieldServiceRevenue}
    </div>
  );
  let totalComplianceRatePercent = (smry) => (
    <div title={`Total Appt Compliance %: ${smry.totalComplianceRatePercent}`} style={{ textAlign: "right" }}>
      {smry.totalComplianceRatePercent}%
    </div>
  );
  let totalSpecialtyCasesOverDuePercent = (smry) => (
    <div
      title={`Total Specialty Cases OverDue %: ${smry.totalSpecialtyCasesOverDuePercent}`}
      style={{ textAlign: "right" }}
    >
      {smry.totalSpecialtyCasesOverDuePercent}%
    </div>
  );
  let totalAppointmentCasesOverDuePercent = (smry) => (
    <div
      title={`Total Appointment Cases OverDue %: ${smry.totalAppointmentCasesOverDuePercent}`}
      style={{ textAlign: "right" }}
    >
      {smry.totalAppointmentCasesOverDuePercent}%
    </div>
  );
  let totalExteriorCasesOverDuePercent = (smry) => (
    <div
      title={`Total Exterior Cases OverDue %: ${smry.totalExteriorCasesOverDuePercent}`}
      style={{ textAlign: "right" }}
    >
      {smry.totalExteriorCasesOverDuePercent}%
    </div>
  );
  let averageLastCheckInDays = (smry) => (
    <div title={`Average Last Check in Days: ${smry.averageLastCheckInDays}`} style={{ textAlign: "right" }}>
      {smry.averageLastCheckInDays}
    </div>
  );
  // careful changing the keys here because they are used in FieldRepTimeCardPage to filter columns
  return [
    {
      key: "isTrainee",
      name: "T",
      width: 24,
      dataSource: (row) => (row.isTrainee ? "T" : ""),
      filterType: FILTER_TYPE_NONE,
      tooltip: (row) => (row.isTrainee ? "Trainee" : ""),
    },
    {
      key: "name",
      code: COL_CODE_FULL_NAME,
      dataSource: "employeeName",
      filterType: FILTER_TYPE_TEXT,
      width: null, // Specifying this because full name col comes with a width by default, this will override it and make it so the name column stretches to fill width
    },
    {
      key: "logisticsManager",
      name: "Logistics Manager",
      dataSource: "logisticsManager",
      filterType: FILTER_TYPE_TEXT,
      width: 140,
    },
    {
      key: "fieldRepTimeEfficiencyGradeLetter",
      name: "Grade",
      width: 55,
      filterType: FILTER_TYPE_MULTI_SELECT_NOLABEL,
      formatter: FORMATTER_INDENTED,
      dataSource: (row) => (row.score * 100 === 100 ? "P" : "F"),
    },
    {
      key: "approvedIcon",
      name: (
        <div title="Approved" style={{ textAlign: "center" }}>
          <Icon color="primary">check_circle_outline</Icon>
        </div>
      ),
      width: 37,
      dataSource: "isApprovedIcon",
      filterType: FILTER_TYPE_NONE,
      tooltip: approvedToolTip,
    },
    {
      key: "nonProdCosts",
      name: "Non-Prod Cost",
      headerTooltip: "Non-Production Cost - Pay Period",
      width: 80,
      dataSource: (data) => data?.nonProductionCosts,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => nonProdFilterRenderer(summaryData),
    },
    {
      key: "totalCost",
      name: "Total Cost",
      headerTooltip: "Actual Weekly Costs + Non-Production Costs",
      width: 90,
      dataSource: (data) => data?.nonProductionCosts + data?.actualCostTotal,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalTotalCost(summaryData),
    },
    {
      key: "totalHours",
      name: "Hours",
      headerTooltip: "Submitted Hours",
      width: 80,
      dataSource: (data) => data?.hours?.toFixed(2),
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_RAW,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalHours(summaryData),
    },
    {
      key: "adminHours",
      name: "Admin Hours",
      headerTooltip: "Administrative Hours",
      width: 100,
      dataSource: (data) => data?.adminWorkHours?.toFixed(2),
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_NUMBER_RAW,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalAdminHours(summaryData),
    },
    {
      key: "submittedEstimatedHours",
      name: "Avg Submitted",
      headerTooltip: "Average Weekly Submitted Hours - 4 Weeks Rolling",
      dataType: DATA_TYPE_NUMBER,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: (row) => (row.submittedEstimatedHours ? parseFloat(row.submittedEstimatedHours).toFixed(1) : ""),
      width: 80,
      filterRenderer: () => totalSubmittedHours(summaryData),
      tooltip: () => "Submitted Hours Average per Week last 4 Weeks",
    },
    {
      key: "assignedEstimatedHours",
      name: "FHE Queue",
      headerTooltip: "Total Field Hours Expected - All Assigned",
      dataType: DATA_TYPE_NUMBER,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: (row) => (row.assignedEstimatedHours ? parseFloat(row.assignedEstimatedHours).toFixed(1) : ""),
      width: 80,
      filterRenderer: () => totalAssignedHours(summaryData),
      tooltip: () => "Field Hours Estimated in Queue",
    },
    {
      key: "weeksEmployed",
      name: "Tenure",
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: "weeksEmployed",
      dataType: DATA_TYPE_NUMBER,
      width: 80,
      tooltip: () => "Weeks in Production",
      filterRenderer: () => averageWeeksEmployed(summaryData),
    },
    {
      key: "state",
      code: COL_CODE_STATE,
      dataSource: "inspectorState",
      filterType: FILTER_TYPE_TEXT,
      width: 60,
    },
    {
      key: "expectedCost",
      name: "Exp Cost",
      headerTooltip: "Expected Weekly Costs - Pay Period",
      width: 90,
      dataSource: (data) => data?.expectedCostTotal,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalExpectedCost(summaryData),
      tooltip: () => "Expected Cost",
    },
    {
      key: "expectedCostToRevenue",
      name: "Exp Cost % Revenue",
      headerTooltip: "Expected Cost % Revenue - Pay Period",
      width: 80,
      dataSource: (data) => data?.expectedCostToRevenue,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalExpectedCostToRevenue(summaryData),
      tooltip: () => "Expected Cost Percent to Revenue",
    },
    {
      key: "actualCost",
      name: "Actual Cost",
      headerTooltip: "Actual Weekly Costs - Pay Period",
      width: 90,
      dataSource: (data) => data?.actualCostTotal,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_MONEY,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalActualCost(summaryData),
      tooltip: () => "Actual Cost",
    },
    {
      key: "actualCostToRevenue",
      name: "Actual Cost % Revenue",
      headerTooltip: "Actual Cost % Revenue - Pay Period",
      width: 80,
      dataSource: (data) => data?.actualCostToRevenue,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalActualCostToRevenue(summaryData),
      tooltip: () => "Actual Cost percent to Revenue",
    },
    {
      key: "inspectorCostEfficiency",
      name: "Exp/Act",
      headerTooltip: "Expected Cost % Revenue/Actual Cost % Revenue - Pay Period",
      width: 80,
      dataSource: (data) => data?.inspectorCostEfficiency,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalCostEfficiency(summaryData),
      tooltip: () => "Expected Cost / Actual Cost..aka Cost Efficiency",
    },
    {
      key: "fieldProductionCostDifference",
      name: "Cost Diff.",
      headerTooltip: "Cost Difference (Expected Costs Less Actual Costs) - Pay Period",
      dataType: DATA_TYPE_MONEY,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: (data) => data?.fieldProductionCostDifference,
      width: 90,
      filterRenderer: () => totalFieldProductionCostDifference(summaryData),
      tooltip: () => "Expected Cost - Actual Cost",
    },
    {
      key: "expectedFieldServiceRevenue",
      name: "Revenue",
      headerTooltip: "Revenue - Pay Period",
      dataType: DATA_TYPE_MONEY,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: (data) => data?.expectedFieldServiceRevenue,
      width: 90,
      filterRenderer: () => totalExpectedFieldServiceRevenue(summaryData),
      tooltip: () => "Revenue",
    },
    {
      key: "complianceRate",
      name: "Appt Compliance %",
      headerTooltip: "Appt Compliance % - Pay Period",
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: (data) => data?.complianceRate,
      width: 90,
      filterRenderer: () => totalComplianceRatePercent(summaryData),
      tooltip: () => "Appt Compliance %",
    },
    {
      key: "specialtyCasesOverDuePercent",
      name: "Toxic Spc.",
      headerTooltip: "Total % of Assigned Specialty Cases Over Due",
      width: 80,
      dataSource: (data) => data?.specialtyCasesOverDuePercent,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalSpecialtyCasesOverDuePercent(summaryData),
      tooltip: () => "Specialty Cases Overdue Percent",
    },
    {
      key: "appointmentCasesOverDuePercent",
      name: "Toxic Apts.",
      headerTooltip: "Total % of Assigned Appointment Cases Over Due",
      width: 80,
      dataSource: (data) => data?.appointmentCasesOverDuePercent,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalAppointmentCasesOverDuePercent(summaryData),
      tooltip: () => "Appointment Cases Overdue Percent",
    },
    {
      key: "exteriorCasesOverDuePercent",
      name: "Toxic",
      headerTooltip: "Total % of Assigned Exterior Cases Over Due",
      width: 80,
      dataSource: (data) => data?.exteriorCasesOverDuePercent,
      filterType: FILTER_TYPE_NONE,
      dataType: DATA_TYPE_PERCENT_DECIMAL,
      formatter: FORMATTER_RIGHT_ALIGN,
      filterRenderer: () => totalExteriorCasesOverDuePercent(summaryData),
      tooltip: () => "Exterior Cases Overdue Percent",
    },
    {
      key: "lastCheckInDays",
      name: "Check Days",
      headerTooltip: "Days Since Last Check In",
      filterType: FILTER_TYPE_NONE,
      formatter: FORMATTER_RIGHT_ALIGN,
      dataSource: (data) => data?.lastCheckInDays,
      dataType: DATA_TYPE_NUMBER,
      width: 80,
      tooltip: () => "Last Check in Days ago",
      filterRenderer: () => averageLastCheckInDays(summaryData),
    },
    {
      key: "lastEmployeeNote",
      name: "Training and Misc",
      filterType: FILTER_TYPE_NONE,
      dataSource: (row) =>
        row.lastEmployeeNote ? `${dateToString(row.lastEmployeeNoteDate)}: ${row.lastEmployeeNote}` : "",
    },
  ];
};
export const calculateLetterGrade = (score, failing_F_Score) => {
  const letterGrades = ["F", "D-", "D", "D+", "C-", "C", "C+", "B-", "B", "B+", "A-", "A", "A+"];
  if (isNaN(parseFloat(score)) || score === "" || score < 0.0) return "";
  if (score <= failing_F_Score) return letterGrades[0];
  const gradeIncrement = (100.0 - failing_F_Score) / letterGrades.length;
  const gradeLocation = (score - failing_F_Score) / gradeIncrement;
  let gradeIndex = parseInt(Math.ceil(gradeLocation));
  if (gradeIndex > letterGrades.length - 1) {
    gradeIndex = letterGrades.length - 1;
  }
  const gradeLetter = letterGrades[gradeIndex];
  return gradeLetter;
};

export const transformEmployeeList = (rawData, filter = () => true) => {
  return rawData
    ? rawData.filter(filter).map((x) => ({
        ...x,
        appraiserTimeEfficiencyScore: x.appraiserTimeEfficiencyScore && x.appraiserTimeEfficiencyScore.toFixed(1),
        totalHours: (x.payrollTotalHours || 0).toFixed(2),
        appraiserExpectedHours: ((x.totalAppraiserExpectedMinutes || 0) / 60.0).toFixed(2),
        hasHours: (x.totalClockedInTimeMinutes || x.totalCaseTimeLogTimeMinutes || 0) > 0,
        IsClockedInNowIcon: x.isClockedInNow ? (
          <div style={{ textAlign: "center" }}>
            <Icon color="primary" className="qa-list-is-clocked-in">
              lens
            </Icon>
          </div>
        ) : (
          []
        ),
        isApprovedIcon: x.isApproved ? <Icon color="primary">check_circle_outline</Icon> : [],
        isMissingClockInfoIcon: x.isMissingClockInfo ? <Icon color="primary">timer_off</Icon> : [],
        anyIncompleteAppraiserIcon: x.anyIncompleteAppraiser ? <Icon>cancel</Icon> : [],
        appraiserTimeEfficiencyGradeLetter: calculateLetterGrade(
          x.appraiserTimeEfficiencyScore,
          x.appraiserTimeEfficiencyFailingScore,
        ),
        qaTimeEfficiencyScore: x.qaTimeEfficiencyScore && x.qaTimeEfficiencyScore.toFixed(0),
        qaProductivityScore: x.qaProductivityScore && x.qaProductivityScore.toFixed(0),
        payrateType: x.payrateType,
      }))
    : [];
};

export const transformClockInOutData = (rawData) => {
  return rawData.map((x) => ({
    date: dateToStringWithDOW(x.clockInDate),
    in: x.clockInDate && moment(new Date(x.clockInDate)).format("h:mm A"),
    out: x.clockOutDate && moment(new Date(x.clockOutDate)).format("h:mm A"),
    hours: (x.timeMinutes / 60).toFixed(2),
  }));
};

export const transformClockInOutDataEditable = (
  rawData,
  handleEditRowFunc,
  handleDeleteRowFunc,
  payPeriod,
  filterDate,
) => {
  return rawData
    .filter((x) => filterOnFilterDate(filterDate, x.clockInDate) || filterOnFilterDate(filterDate, x.clockOutDate))
    .map((x) => ({
      date: dateToStringWithDOW(x.clockInDate),
      in: x.clockInDate && moment(new Date(x.clockInDate)).format("h:mm A"),
      out: x.clockOutDate && moment(new Date(x.clockOutDate)).format("h:mm A"),
      hours: (x.timeMinutes / 60).toFixed(2),
      wasEdited: x.wasEdited ? (
        <div style={{ textAlign: "center" }}>
          {updateIcon}
        </div>
      ) : (
        []
      ),
      rawData: dateToStringWithDOW(x.clockInDate),
      button: (
        <div className="edit-col">
          <IconButton
            onClick={() => handleEditRowFunc(x.id)}
            disabled={isPayPeriodClosed(payPeriod)}
            title="Edit"
            size="small"
          >
            <Icon>create</Icon>
          </IconButton>
          <IconButton
            onClick={() => handleDeleteRowFunc(x.id)}
            disabled={isPayPeriodClosed(payPeriod)}
            title="Delete"
            size="small"
          >
            <Icon>delete</Icon>
          </IconButton>
        </div>
      ),
    }));
};

export const transformAppraiserPerformanceData = (rawData) => {
  // Aggregate the detailed rows into a single row by control number
  if (!Array.isArray(rawData)) return null;
  const map = new Map();
  const retVal = [];
  for (const item of rawData) {
    if (!map.has(item.controlIdNumber)) {
      map.set(item.controlIdNumber, {
        ...item,
        workTypePreviousMinutes: item.workTypePreviousMinutes || 0,
        workTypePreviousMinutesForUser: item.workTypePreviousMinutesForUser || 0,
        isAppraiserIncomplete: !item.isAppraiserCompleted,
      });
      continue;
    }
    const mapItem = map.get(item.controlIdNumber);
    mapItem.timeMinutes += item.timeMinutes;
  }
  for (const item of map.values()) {
    retVal.push({
      ...item,
      totalMinutes: item.workTypePreviousMinutesForUser + item.timeMinutes,
      isAppraiserIncompleteIcon: item.isAppraiserIncomplete ? <Icon>cancel</Icon> : [],
    });
  }
  return retVal;
};

export const transformProductionTimeData = (rawData) => {
  return rawData.map((x) => ({
    ...x,
    timeMinutes: x.timeMinutes.toFixed(2),
  }));
};

export const transformProductionTimeDataEditable = (rawData, handleEditRowFunc, handleDeleteRowFunc, payPeriod) => {
  return rawData.map((x) => ({
    ...x,
    timeMinutes: x.timeMinutes.toFixed(2),
    button: (
      <div className="edit-col">
        <IconButton
          onClick={() => handleEditRowFunc(x)}
          disabled={isPayPeriodClosed(payPeriod)}
          title="Edit"
          size="small"
        >
          <Icon>create</Icon>
        </IconButton>
        <IconButton
          onClick={() => handleDeleteRowFunc(x.id)}
          disabled={isPayPeriodClosed(payPeriod)}
          title="Delete"
          size="small"
        >
          <Icon>delete</Icon>
        </IconButton>
      </div>
    ),
  }));
};
export const clockModeEnum = {
  CLOCK_IN: "Clock In",
  CLOCK_OUT: "Clock Out",
};
export const isPayPeriodClosed = (payPeriod) => {
  if (payPeriod.closedDate !== null) {
    return true;
  }
  return false;
};
export const qualityAssurancePerformanceColDefs = [
  {
    name: "Control #",
    code: COL_CODE_CONTROL_NUMBER,
    dataType: DATA_TYPE_TEXT_BUTTON,
    dataSource: "controlIdNumber",
    filterType: FILTER_TYPE_NONE,
    tooltip: "Click to view case",
  },
  {
    key: "surveyTypeName",
    name: "Survey Type",
    width: 200,
    dataSource: "surveyTypeName",
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "previousMinutes",
    name: "Previous Mins",
    width: 120,
    dataSource: "workTypePreviousMinutesForUser",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Previous Mins</div>,
  },
  {
    key: "currentMinutes",
    name: "Current Mins",
    width: 120,
    dataSource: "timeMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Current Mins</div>,
  },
  {
    key: "totalMinutes",
    name: "Total Mins",
    width: 120,
    dataSource: "totalMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Total Mins</div>,
  },
  {
    key: "qaReviewExpectedMinutes",
    name: "Expected Mins",
    width: 120,
    dataSource: "qaReviewExpectedMinutes",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Expected Mins</div>,
  },
  {
    key: "qaReviewEfficiency",
    name: "Efficiency %",
    width: 120,
    dataSource: "qaReviewEfficiency",
    filterType: FILTER_TYPE_NONE,
    dataType: DATA_TYPE_NUMBER,
    formatter: FORMATTER_RIGHT_ALIGN,
    headerrenderer: <div className="right-align-header">Efficiency %</div>,
  },
];
export const transformQualityAssurancePerformanceData = (rawData) => {
  // Aggregate the detailed rows into a single row by control number
  if (!Array.isArray(rawData)) return null;
  const map = new Map();
  for (const item of rawData) {
    const key = `${item.controlIdNumber}-${item.workTypeId}`;
    if (!map.has(key)) {
      map.set(key, {
        ...item,
        workTypePreviousMinutes: item.workTypePreviousMinutes || 0,
        workTypePreviousMinutesForUser: item.workTypePreviousMinutesForUser || 0,
      });
      continue;
    }
    const mapItem = map.get(key);
    mapItem.timeMinutes += item.timeMinutes;
  }
  const retVal = [];
  for (const item of map.values()) {
    if (
      CaseTimeLogWorkTypeEnum.FIELD_REVIEW === item.workTypeId ||
      (CaseTimeLogWorkTypeEnum.FS_REVIEW_AUDIT === item.workTypeId && !item.isFieldReviewCompleted)
    )
      continue;
    if (CaseTimeLogWorkTypeEnum.VALUATION_REVIEW === item.workTypeId && !item.isAppraisalReviewCompleted) continue;
    if (CaseTimeLogWorkTypeEnum.VALUATION === item.workTypeId && !item.isAppraiserCompleted) continue;
    const expectedMinutes = expectedMinutesFromCaseTimeLogDto(item);
    let newItem = {
      ...item,
      totalMinutes: item.workTypePreviousMinutesForUser + item.timeMinutes,
      qaReviewExpectedMinutes: expectedMinutes,
      qaReviewEfficiency:
        item.workTypePreviousMinutesForUser + item.timeMinutes > 0
          ? ((expectedMinutes / (item.workTypePreviousMinutesForUser + item.timeMinutes)) * 100.0).toFixed(0)
          : null,
    };
    newItem.workTypePreviousMinutesForUser = newItem.workTypePreviousMinutesForUser.toFixed(1);
    newItem.timeMinutes = newItem.timeMinutes.toFixed(1);
    newItem.totalMinutes = newItem.totalMinutes.toFixed(1);
    newItem.qaReviewExpectedMinutes = newItem.qaReviewExpectedMinutes.toFixed(1);
    retVal.push(newItem);
  }
  return retVal;
};
const expectedMinutesFromCaseTimeLogDto = (item) => {
  return CaseTimeLogWorkTypeEnum.FIELD_REVIEW === item.workTypeId ||
    CaseTimeLogWorkTypeEnum.FS_REVIEW_AUDIT === item.workTypeId
    ? item.fieldReviewExpectedMinutes
    : CaseTimeLogWorkTypeEnum.VALUATION_REVIEW === item.workTypeId
      ? item.appraisalReviewExpectedMinutes
      : CaseTimeLogWorkTypeEnum.VALUATION === item.workTypeId
        ? item.appraisalExpectedMinutes
        : 0;
};

export const transformFieldRepManagerSummary = (data) => {
  let formatMoney = moneyFormatter();

  let totalNonProdCost = 0;
  let totalHours = 0;
  let totalAdminHours = 0;
  let totalSubmittedHours = 0;
  let totalAssignedHours = 0;
  let totalExpectedCost = 0;
  let totalActualCost = 0;
  let totalExpectedFieldServiceRevenue = 0;
  let totalWeeksEmployed = 0;
  let count = (data && data.length) || 0;
  let countOfCheckIns = 0;
  let totalSpecialtyCases = 0;
  let totalSpecialtyCasesOverDue = 0;
  let totalAppointmentCases = 0;
  let totalAppointmentCasesOverDue = 0;
  let totalExteriorCases = 0;
  let totalExteriorCasesOverDue = 0;
  let totalLastCheckInDays = 0;
  let totalComplianceCount = 0;
  let totalComplianceCumulative = 0;
  data &&
    data.forEach((item) => {
      totalNonProdCost += item.nonProductionCosts || 0.0;
      totalHours += item.hours || 0.0;
      totalAdminHours += item.adminWorkHours || 0;
      totalSubmittedHours += Math.round(item.submittedEstimatedHours * 10) / 10 || 0.0;
      totalAssignedHours += Math.round(item.assignedEstimatedHours * 10) / 10 || 0.0;
      totalExpectedCost += item.expectedCostTotal || 0.0;
      totalActualCost += item.actualCostTotal || 0.0;
      totalExpectedFieldServiceRevenue += Math.round(item.expectedFieldServiceRevenue * 100) / 100 || 0;
      totalWeeksEmployed += item.weeksEmployed || 0;
      totalSpecialtyCases += item.specialtyCasesCount || 0;
      totalSpecialtyCasesOverDue += item.specialtyCasesOverDueCount || 0;
      totalAppointmentCases += item.appointmentCasesCount || 0;
      totalAppointmentCasesOverDue += item.appointmentCasesOverDueCount || 0;
      totalExteriorCases += item.exteriorCasesCount || 0;
      totalExteriorCasesOverDue += item.exteriorCasesOverDueCount || 0;
      totalLastCheckInDays += item.lastCheckInDays || 0;
      countOfCheckIns += item.lastCheckInDays == null ? 0 : 1;
      totalComplianceCount += item.complianceRate != null ? 1 : 0;
      totalComplianceCumulative += item.complianceRate || 0;
    });
  return {
    totalNonProdCost: formatMoney(totalNonProdCost),
    totalHours: totalHours.toFixed(2),
    totalAdminHours: totalAdminHours.toFixed(2),
    totalSubmittedHours: numberWithCommasFormatter(totalSubmittedHours.toFixed(1)),
    totalAssignedHours: numberWithCommasFormatter(totalAssignedHours.toFixed(1)),
    totalExpectedCost: formatMoney(totalExpectedCost),
    totalActualCost: formatMoney(totalActualCost),
    totalTotalCost: formatMoney(totalActualCost + totalNonProdCost),
    totalExpectedFieldServiceRevenue: formatMoney(totalExpectedFieldServiceRevenue),
    totalExpectedCostToRevenue:
      totalExpectedFieldServiceRevenue === 0
        ? 0
        : ((totalExpectedCost / totalExpectedFieldServiceRevenue) * 100).toFixed(0),
    totalActualCostToRevenue:
      totalExpectedFieldServiceRevenue === 0
        ? 0
        : ((totalActualCost / totalExpectedFieldServiceRevenue) * 100).toFixed(0),
    totalCostEfficiency: totalActualCost === 0 ? 0 : ((totalExpectedCost / totalActualCost) * 100).toFixed(0),
    averageWeeksEmployed: count === 0 ? 0 : (totalWeeksEmployed / count).toFixed(0) || 0,
    totalFieldProductionCostDifference: formatMoney(totalExpectedCost - totalActualCost),
    totalComplianceRatePercent:
      totalComplianceCount === 0 ? 0 : ((totalComplianceCumulative / totalComplianceCount) * 100).toFixed(0),
    totalSpecialtyCasesOverDuePercent:
      totalSpecialtyCases === 0 ? 0 : ((totalSpecialtyCasesOverDue / totalSpecialtyCases) * 100).toFixed(0),
    totalAppointmentCasesOverDuePercent:
      totalAppointmentCases === 0 ? 0 : ((totalAppointmentCasesOverDue / totalAppointmentCases) * 100).toFixed(0),
    totalExteriorCasesOverDuePercent:
      totalExteriorCases === 0 ? 0 : ((totalExteriorCasesOverDue / totalExteriorCases) * 100).toFixed(0),
    averageLastCheckInDays: countOfCheckIns === 0 ? 0 : (totalLastCheckInDays / countOfCheckIns).toFixed(0) || 0,
  };
};
